import AbstractApi from "./AbstractApi";
import bowser from "bowser";

/**
 * Xeerpa social login helper class.
 * @author Mendieta
 * @class Xeerpa
 * @namespace net.api
 */
export default class Xeerpa extends AbstractApi {
  static apiName = "Xeerpa";

  static apiURL = "https://auth-cuamoc.xeerpa.com";

  /**
   * Facebook static string ID
   * @default 'FB'
   * @property FB
   * @static
   * @type {string}
   */
  static FB = "FB";

  /**
   * Callback to be called after Xeerpa response.
   * @property cb
   * @default null
   * @type {function}
   */
  static cb = null;

  static _load() {
    this._init();
  }

  /**
   * Initiates a login to a social network with Xeerpa.
   * @param {string} sn The social network string ID.
   * @param {Object} [data=null] The extra data to be sent, optional.
   * @param {function} resolve
   * @param {function} reject
   * @method login
   */
  static login(resolve, reject, data = {}, sn = "") {
    this._login(resolve, reject, data, sn);
  }

  static loginAsync(data = null, sn = this.FB) {
    return new Promise((resolve, reject) => {
      this._login(resolve, reject, data, sn);
    });
  }

  static _login(resolve, reject, data = {}, sn = "") {
    this.timeout = undefined;
    this.resolveCb = resolve;
    const url = "/socialLogin/facebook";
    const apiURL = `${url}?socialNetwork=${sn}&data=${data}`;
    window.addEventListener("message", this._messageHandler, false);
    window.open(
      apiURL,
      "Login",
      "width=" + 500 + ", height=" + 475 + ", scrollbars=yes",
    );
    if (
      navigator.appName === "Microsoft Internet Explorer" ||
      !!(
        navigator.userAgent.match(/Trident/) ||
        navigator.userAgent.match(/rv 11/)
      ) ||
      bowser.msie
    ) {
      this._cookieInterval = window.setInterval(() => {
        let data = this._readCookie();
        if (data) {
          window.clearInterval(this._cookieInterval);
          this._receiveData(data);
        }
      }, 100);
    }
    this._loginTimeout = window.setTimeout(() => {
      if (typeof this._cookieInterval === "number")
        window.clearInterval(this._cookieInterval);
      reject(new Error("Xeerpa login timeout was reached."));
    }, 3e4);
  }

  static _linkData() {
    var newData = {};
    if (Xeerpa.userData.user.email) {
      Xeerpa._addNewId();
      newData["EMAILHMEX"] = Xeerpa.userData.user.email;
    }
    if (Xeerpa._getKruxId()) {
      newData["kruxId"] = Xeerpa._getKruxId();
    }
    Xeerpa._addData(newData);
  }

  static _getKruxId() {
    if (window.localStorage) {
      return window.localStorage.getItem("kxheineken_mx_kuid");
    } else {
      return null;
    }
  }

  static _addNewId() {
    fetch(
      Xeerpa.apiURL +
        "/auth/newId?id=" +
        Xeerpa.userData.id +
        "&token=" +
        Xeerpa.userData.token +
        "&newId=" +
        Xeerpa.sha256(Xeerpa.userData.user.email),
    );
  }

  static _addData(data) {
    sessionStorage.setItem("usi", Xeerpa.userData.id);
    sessionStorage.setItem("ust", Xeerpa.userData.token);
    fetch(
      Xeerpa.apiURL +
        "/auth/data?id=" +
        Xeerpa.userData.id +
        "&token=" +
        Xeerpa.userData.token +
        "&data=" +
        JSON.stringify(data),
    );
  }

  /**
   * Event handler for window message events.
   * @param {Event} event The event to be handled.
   * @private
   * @method _messageHandler
   */
  static _messageHandler = event => {
    if (typeof event.data !== "string") return;
    let data = JSON.parse(event.data);
    if (data.socialNetwork) Xeerpa._receiveData(data);
  };

  /**
   * Reads the cookie stored, and returns the value when appropriate.
   * @returns {Object} The user object data
   * @private
   * @method _readCookie
   */
  static _readCookie() {
    if (!document.cookie) return;
    let cookies = document.cookie.split("");
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].split("=");
      if (cookie[0].trim() === "user") {
        let value = cookie[1];
        value = JSON.parse(value);
        document.cookie =
          "user=path=/expires=" + new Date(Date.now() - 1000).toGMTString();
        return value;
      }
    }
  }

  /**
   * Process the data sent from Xeerpa and calls callback.
   * @param {Object} data The object to be processed.
   * @private
   * @method _receiveData
   */
  static _receiveData(data) {
    // TODO: Conditionally remove listener, IE doesn't use it.
    window.removeEventListener("message", this._messageHandler);
    this._loginTimeout && window.clearTimeout(this._loginTimeout);
    Xeerpa.userData = data;
    Xeerpa._linkData();
    this.resolveCb(data);
  }

  static sha256(ascii) {
    function rightRotate(value, amount) {
      return (value >>> amount) | (value << (32 - amount));
    }

    var mathPow = Math.pow;
    var maxWord = mathPow(2, 32);
    var lengthProperty = "length";
    var i, j; // Used as a counter across the whole file
    var result = "";

    var words = [];
    var asciiBitLength = ascii[lengthProperty] * 8;

    //* caching results is optional - remove/add slash from front of this line to toggle
    // Initial hash value: first 32 bits of the fractional parts of the square roots of the first 8 primes
    // (we actually calculate the first 64, but extra values are just ignored)
    var hash = [];
    // Round constants: first 32 bits of the fractional parts of the cube roots of the first 64 primes
    var k = [];
    var primeCounter = k[lengthProperty];

    var isComposite = {};
    for (var candidate = 2; primeCounter < 64; candidate++) {
      if (!isComposite[candidate]) {
        for (i = 0; i < 313; i += candidate) {
          isComposite[i] = candidate;
        }
        hash[primeCounter] = (mathPow(candidate, 0.5) * maxWord) | 0;
        k[primeCounter++] = (mathPow(candidate, 1 / 3) * maxWord) | 0;
      }
    }

    ascii += "\x80"; // Append Ƈ' bit (plus zero padding)
    while ((ascii[lengthProperty] % 64) - 56) ascii += "\x00"; // More zero padding
    for (i = 0; i < ascii[lengthProperty]; i++) {
      j = ascii.charCodeAt(i);
      if (j >> 8) return; // ASCII check: only accept characters in range 0-255
      words[i >> 2] |= j << (((3 - i) % 4) * 8);
    }
    words[words[lengthProperty]] = (asciiBitLength / maxWord) | 0;
    words[words[lengthProperty]] = asciiBitLength;

    // process each chunk
    for (j = 0; j < words[lengthProperty]; ) {
      var w = words.slice(j, (j += 16)); // The message is expanded into 64 words as part of the iteration
      var oldHash = hash;
      // This is now the undefinedworking hash', often labelled as variables a...g
      // (we have to truncate as well, otherwise extra entries at the end accumulate
      hash = hash.slice(0, 8);

      for (i = 0; i < 64; i++) {
        // var i2 = i + j
        // Expand the message into 64 words
        // Used below if
        var w15 = w[i - 15];
        var w2 = w[i - 2];

        // Iterate
        var a = hash[0];
        var e = hash[4];
        var temp1 =
          hash[7] +
          (rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25)) + // S1
          ((e & hash[5]) ^ (~e & hash[6])) + // ch
          k[i] +
          // Expand the message schedule if needed
          (w[i] =
            i < 16
              ? w[i]
              : (w[i - 16] +
                (rightRotate(w15, 7) ^ rightRotate(w15, 18) ^ (w15 >>> 3)) + // s0
                  w[i - 7] +
                  (rightRotate(w2, 17) ^ rightRotate(w2, 19) ^ (w2 >>> 10))) | // s1
                0);
        // This is only used once, so *could* be moved below, but it only saves 4 bytes and makes things unreadble
        var temp2 =
          (rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22)) + // S0
          ((a & hash[1]) ^ (a & hash[2]) ^ (hash[1] & hash[2])); // maj

        hash = [(temp1 + temp2) | 0].concat(hash); // We don't bother trimming off the extra ones, they're harmless as long as we're truncating when we do the slice()
        hash[4] = (hash[4] + temp1) | 0;
      }

      for (i = 0; i < 8; i++) {
        hash[i] = (hash[i] + oldHash[i]) | 0;
      }
    }

    for (i = 0; i < 8; i++) {
      for (j = 3; j + 1; j--) {
        var b = (hash[i] >> (j * 8)) & 255;
        result += (b < 16 ? 0 : "") + b.toString(16);
      }
    }
    return result;
  }
}
